import React from "react";
import PropTypes from "prop-types";

/**
 * "Showing 1–20 of 180" plus numbered pages with an ellipsis, per the design.
 * (Bridge only has prev/next arrows.)
 */
const ELLIPSIS = "…";

/**
 * First page, last page, and a run of three around the current one, elided with
 * an ellipsis. The window slides rather than shrinking at the ends, so page 1 of
 * 9 reads "1 2 3 … 9" like the design rather than "1 2 … 9".
 */
const WINDOW = 3;

export const pageItems = (current, total) => {
  if (total <= WINDOW + 4) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  let start = Math.max(1, current - Math.floor(WINDOW / 2));
  const end = Math.min(total, start + WINDOW - 1);
  start = Math.max(1, end - WINDOW + 1);

  const pages = new Set([1, total]);
  for (let p = start; p <= end; p += 1) pages.add(p);

  const sorted = [...pages].sort((a, b) => a - b);
  const out = [];
  sorted.forEach((p, i) => {
    if (i > 0 && p - sorted[i - 1] > 1) out.push(ELLIPSIS);
    out.push(p);
  });
  return out;
};

const Pagination = ({ pageNum, pageSize, totalCount, onChange }) => {
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const from = (pageNum - 1) * pageSize + 1;
  const to = Math.min(pageNum * pageSize, totalCount);
  /* A page PAST THE END has no range to state: `from` comes from the page and
     `to` from the total, so the two cross over and it printed backwards —
     "Showing 41–20 of 20". The bar itself is deliberately still drawn there (it
     is the way back to page 1), so this says plainly that nothing is on show
     rather than clamping `from` to a row it is not displaying. */
  const empty = to < from;

  return (
    <div className="pagination">
      <span className="showing">
        {empty ? (
          <>
            Showing <strong>0</strong> of <strong>{totalCount}</strong>
          </>
        ) : (
          <>
            Showing <strong>{from}–{to}</strong> of <strong>{totalCount}</strong>
          </>
        )}
      </span>

      <div className="pages">
        <button
          type="button"
          className="page-arrow"
          aria-label="Previous page"
          disabled={pageNum <= 1}
          onClick={() => onChange(pageNum - 1)}
        >
          &#8249;
        </button>

        {pageItems(pageNum, totalPages).map((p, i) =>
          p === ELLIPSIS ? (
            <span className="page-gap" key={`gap-${i}`}>
              {ELLIPSIS}
            </span>
          ) : (
            <button
              type="button"
              key={p}
              className={`page-num${p === pageNum ? " is-current" : ""}`}
              aria-current={p === pageNum ? "page" : undefined}
              onClick={() => onChange(p)}
            >
              {p}
            </button>
          )
        )}

        <button
          type="button"
          className="page-arrow"
          aria-label="Next page"
          disabled={pageNum >= totalPages}
          onClick={() => onChange(pageNum + 1)}
        >
          &#8250;
        </button>
      </div>
    </div>
  );
};

Pagination.propTypes = {
  pageNum: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  totalCount: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default Pagination;
